import type { LinkedItem, LinkedItemSon, TiradorWithPositions } from '@types'

type TiradorParent = Pick<
  LinkedItem,
  'tipoNormalizado' | 'hand' | 'width' | 'height' | 'thickness' | 'morphs' | 'childs'
>

type TiradorChild = TiradorWithPositions &
  Pick<LinkedItemSon, 'handPosition' | 'typeOfSon' | 'description' | 'id'>

type MorphLike = {
  name: string
  value?: number
  default?: number
  _default?: number
}

/** Dimensión actual del morph: prioriza value (tras resize) sobre default. */
export function getMorphActualDimension(
  morph: MorphLike | undefined,
  parentFallback: number,
): number {
  return morph?.value ?? morph?.default ?? parentFallback
}

/** Dimensión base de referencia del morph (valor de catálogo). */
export function getMorphBaseDimension(
  morph: MorphLike | undefined,
  parentFallback: number,
): number {
  return morph?._default ?? parentFallback
}

export function calculateTiradorPosition(
  childItem: TiradorChild,
  parentItem: TiradorParent,
): { x: number; y: number; z: number } {
  const parentMorphs = parentItem.morphs || []
  const morphAncho = parentMorphs.find((m) => m.name === 'Ancho')
  const morphAlto = parentMorphs.find((m) => m.name === 'Alto')
  const morphFondo = parentMorphs.find((m) => m.name === 'Fondo')
  const widthBase = getMorphBaseDimension(morphAncho, parentItem.width)
  const widthActual = getMorphActualDimension(morphAncho, parentItem.width)
  const heightBase = getMorphBaseDimension(morphAlto, parentItem.height)
  const heightActual = getMorphActualDimension(morphAlto, parentItem.height)
  const depthBase = getMorphBaseDimension(morphFondo, parentItem.thickness)
  const depthActual = getMorphActualDimension(morphFondo, parentItem.thickness)
  let prefix = 'b'
  if (parentItem.tipoNormalizado === 'MAL' || parentItem.tipoNormalizado === 'COL') prefix = 'a'
  let Xbase: number | undefined
  let Ybase: number | undefined
  let Zbase: number | undefined
  const parentHand = parentItem.hand
  const isCentralHandNormal =
    parentHand === 'CN' &&
    (childItem.handPosition === 'Izquierda' || childItem.handPosition === 'Derecha')

  const isCNWithCenterPosition = parentHand === 'CN' && childItem.handPosition === 'Centro'
  let tiradorIndex = -1
  let isTirador = false
  if (isCNWithCenterPosition) {
    isTirador = childItem.typeOfSon === 'Tirador' || childItem.description === 'Tirador'

    if (isTirador) {
      const allChilds = Array.from(parentItem.childs || [])
      const currentIndex = allChilds.indexOf(childItem.id)

      if (currentIndex === 0) {
        tiradorIndex = 0
      } else if (currentIndex === 1) {
        tiradorIndex = 1
      } else {
        tiradorIndex = currentIndex
      }
    }
  }

  if (childItem.handPosition === 'Izquierda') {
    if (parentHand === 'CN') {
      Xbase = childItem[`${prefix}iPosicionX`]
      Ybase = childItem[`${prefix}iPosicionY`]
      Zbase = childItem[`${prefix}iPosicionZ`]
    } else if (isCentralHandNormal) {
      Xbase = childItem[`${prefix}dPosicionX`] ?? childItem[`${prefix}cPosicionX`] ?? 0
      Ybase = childItem[`${prefix}dPosicionY`] ?? childItem[`${prefix}cPosicionY`] ?? 0
      Zbase = childItem[`${prefix}dPosicionZ`] ?? childItem[`${prefix}cPosicionZ`] ?? 0
    } else {
      Xbase = childItem[`${prefix}iPosicionX`]
      Ybase = childItem[`${prefix}iPosicionY`]
      Zbase = childItem[`${prefix}iPosicionZ`]
    }
  } else if (childItem.handPosition === 'Centro') {
    if (isCNWithCenterPosition && isTirador) {
      Xbase = childItem[`${prefix}cPosicionX`]
      Ybase = childItem[`${prefix}cPosicionY`]
      Zbase = childItem[`${prefix}cPosicionZ`]
    } else {
      Xbase = childItem[`${prefix}cPosicionX`]
      Ybase = childItem[`${prefix}cPosicionY`]
      Zbase = childItem[`${prefix}cPosicionZ`]
    }
  } else if (childItem.handPosition === 'Derecha') {
    if (parentHand === 'CN') {
      Xbase = childItem[`${prefix}dPosicionX`]
      Ybase = childItem[`${prefix}dPosicionY`]
      Zbase = childItem[`${prefix}dPosicionZ`]
    } else if (isCentralHandNormal) {
      Xbase = childItem[`${prefix}iPosicionX`] ?? childItem[`${prefix}cPosicionX`] ?? 0
      Ybase = childItem[`${prefix}iPosicionY`] ?? childItem[`${prefix}cPosicionY`] ?? 0
      Zbase = childItem[`${prefix}iPosicionZ`] ?? childItem[`${prefix}cPosicionZ`] ?? 0
    } else {
      Xbase = childItem[`${prefix}dPosicionX`]
      Ybase = childItem[`${prefix}dPosicionY`]
      Zbase = childItem[`${prefix}dPosicionZ`]
    }
  } else if (childItem.handPosition === 'Normal') {
    const isFirstTirador =
      childItem ===
      Array.from(parentItem.childs || []).find((childID: unknown) => {
        const child = (parentItem as { items?: Map<string, TiradorChild> }).items?.get(
          childID as string,
        )
        return child && (child.description === 'Tirador' || child.typeOfSon === 'Tirador')
      })
    if (isFirstTirador) {
      Xbase = childItem[`${prefix}dPosicionX`] ?? childItem[`${prefix}cPosicionX`] ?? 0
      Ybase = childItem[`${prefix}dPosicionY`] ?? childItem[`${prefix}cPosicionY`] ?? 0
      Zbase = childItem[`${prefix}dPosicionZ`] ?? childItem[`${prefix}cPosicionZ`] ?? 0
    } else {
      Xbase = childItem[`${prefix}iPosicionX`] ?? childItem[`${prefix}cPosicionX`] ?? 0
      Ybase = childItem[`${prefix}iPosicionY`] ?? childItem[`${prefix}cPosicionY`] ?? 0
      Zbase = childItem[`${prefix}iPosicionZ`] ?? childItem[`${prefix}cPosicionZ`] ?? 0
    }
  }
  Xbase = Xbase ?? 0
  Ybase = Ybase ?? 0
  Zbase = Zbase ?? 0
  let x = Xbase
  let y = Ybase
  let z = Zbase

  if (parentItem.tipoNormalizado === 'MBJ') {
    if (childItem.handPosition === 'Centro') {
      x = Xbase + (widthActual - widthBase) / 2
    } else if (childItem.handPosition === 'Derecha' && !isCentralHandNormal) {
      x = Xbase + (widthActual - widthBase)
    } else if (isCentralHandNormal) {
      x = Xbase + (widthActual - widthBase) / 2
    }
    if (isCentralHandNormal) {
      y = Ybase - (heightBase - heightActual)
      z = Zbase - (depthBase - depthActual)
    } else {
      y = Ybase - (heightBase - heightActual)
      z = Zbase - (depthBase - depthActual)
    }
  } else if (parentItem.tipoNormalizado === 'COL' || parentItem.tipoNormalizado === 'MAL') {
    if (parentHand === 'CN') {
      let originalPos = 0

      if (childItem.handPosition === 'Centro' && isTirador) {
        originalPos = childItem[`${prefix}cPosicionX`] ?? 0
      } else if (childItem.handPosition === 'Izquierda') {
        originalPos = childItem[`${prefix}iPosicionX`] ?? 0
      } else if (childItem.handPosition === 'Derecha') {
        originalPos = childItem[`${prefix}dPosicionX`] ?? 0
      }

      if (originalPos !== 0) {
        const ratio = originalPos / widthBase
        x = ratio * widthActual
      } else {
        x = Xbase
      }
    } else {
      if (childItem.handPosition === 'Centro') {
        x = Xbase + (widthActual - widthBase) / 2
      } else if (childItem.handPosition === 'Derecha') {
        x = Xbase + (widthActual - widthBase)
      }
    }

    if (depthActual !== depthBase) {
      z = Zbase - (depthBase - depthActual)
    }
  }

  return { x, y, z }
}
